import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DevicesClass } from '@app/core/classes';
import { BreadcrumbsDataModel } from '@app/core/models/breadcrumbs';
import { RoutingConfig } from '@app/routing/routing.config';
import { AppStaticURL } from '@app/app.settings';
import { PerformersService } from '@app/modules/account-modules/performers.service';
import { UserPerformerListItemDto } from '@app/modules/account-modules/performers.model';
import { CitiesService } from '@app/modules/cities-module/cities.service';
import { AccountStoryService } from '@app/modules/account-modules/account.story';
import { AccountService } from '@app/modules/account-modules/account.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PERFORMER_EDIT_STATE_SLUG, PERFORMER_EDIT_STATE_IS_PUBLISHED } from '@app/core/services/guard/guard-performer-edit.service';

@UntilDestroy()
@Component({
  selector: 'app-account-performers',
  templateUrl: './performers.component.html',
  styleUrls: ['./performers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PerformersComponent extends DevicesClass implements OnInit {
  /** Maximum number of performer pages allowed per account. */
  readonly MAX_AMOUNT_PERFORMERS = 1;

  public Routing = RoutingConfig;
  public StaticURL = AppStaticURL;

  public BreadcrumbsData: BreadcrumbsDataModel = [
    {
      title: 'Личный кабинет',
      url: '/' + RoutingConfig.Account
    },
    {
      title: 'Мои исполнители',
      url: '/' + RoutingConfig.Account + '/' + RoutingConfig.AccountPerformers,
      active: true
    }
  ];

  performersList: UserPerformerListItemDto[] = [];
  loading = false;
  errorMessage: string = null;
  /** Whether phone is verified (null when account data not loaded yet). From contacts.phoneVerified. */
  phoneVerified: boolean | null = null;
  copiedLinkId: string | null = null;
  /** Name for prefill on create page (1–200 chars). */
  newPerformerName = '';
  private copyMsgTimer: ReturnType<typeof setTimeout> = null;

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private router: Router,
    private performersService: PerformersService,
    private citiesService: CitiesService,
    private accountStory: AccountStoryService,
    private accountService: AccountService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.accountStory.AccountEventBus$.pipe(untilDestroyed(this)).subscribe(emit => {
      if (emit.event === 'onAccountUpdate' && emit.data != null) {
        this.phoneVerified = emit.data.contacts?.phoneVerified ?? false;
        if (this.phoneVerified) {
          this.loadPerformers();
        }
        this.cdr.markForCheck();
      }
      if (emit.event === 'onAccountEmpty' || emit.event === 'onAccountLogout') {
        this.phoneVerified = false;
        this.cdr.markForCheck();
      }
    });
    this.accountService.getMyInfo().pipe(untilDestroyed(this)).subscribe(
      data => {
        this.phoneVerified = data.contacts?.phoneVerified ?? false;
        if (this.phoneVerified) {
          this.loadPerformers();
        }
        this.cdr.markForCheck();
      },
      () => {
        this.phoneVerified = false;
        this.cdr.markForCheck();
      }
    );
  }

  loadPerformers(): void {
    this.loading = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
    this.performersService.getPerformersList().pipe(untilDestroyed(this)).subscribe(
      list => {
        this.performersList = list || [];
        this.loading = false;
        this.cdr.markForCheck();
      },
      err => {
        this.loading = false;
        this.errorMessage = (err && (err.message || err.error)) || 'Не удалось загрузить список.';
        this.cdr.markForCheck();
      }
    );
  }

  getPublicUrl(item: UserPerformerListItemDto): string {
    const slug = (item.slug ?? '').trim();
    const identifier = (slug && !this.performersService.isHostlikeIdentifier(slug)) ? slug : (item.id ?? '');
    return this.performersService.getPublicProfileUrl(identifier);
  }

  onOpenPublic(item: UserPerformerListItemDto): void {
    const url = this.getPublicUrl(item);
    window.open(url, '_blank');
  }

  onEdit(item: UserPerformerListItemDto): void {
    this.router.navigate(
      ['/', this.Routing.Account, this.Routing.AccountPerformers, this.Routing.Edit, item.id],
      {
        state: {
          [PERFORMER_EDIT_STATE_SLUG]: item.slug ?? '',
          [PERFORMER_EDIT_STATE_IS_PUBLISHED]: item.isPublished ?? false
        }
      }
    ).catch(() => {});
  }

  onCopyLink(item: UserPerformerListItemDto): void {
    const url = this.getPublicUrl(item);
    const done = () => {
      this.copiedLinkId = item.id;
      this.cdr.markForCheck();
      if (this.copyMsgTimer) clearTimeout(this.copyMsgTimer);
      this.copyMsgTimer = setTimeout(() => {
        this.copiedLinkId = null;
        this.cdr.markForCheck();
      }, 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => {});
    }
  }

  onTogglePublish(item: UserPerformerListItemDto): void {
    const next = !item.isPublished;
    this.performersService.setPerformerPublished(item.id, next).pipe(untilDestroyed(this)).subscribe(
      () => {
        item.isPublished = next;
        this.cdr.markForCheck();
      },
      () => {
        this.cdr.markForCheck();
      }
    );
  }

  getCityName(item: UserPerformerListItemDto): string {
    if (item.city == null) return '—';
    const city = this.citiesService.onGetCityByDictionary(item.city);
    return city?.name ?? '—';
  }

  formatDate(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  trackByPerformerId(_index: number, item: UserPerformerListItemDto): string {
    return item.id;
  }

  get isMaxPerformersReached(): boolean {
    return this.performersList.length >= this.MAX_AMOUNT_PERFORMERS;
  }

  onCreatePerformer($event: Event): void {
    $event.preventDefault();
    if (this.isMaxPerformersReached) return;
    this.router.navigateByUrl(
      `/${this.Routing.Account}/${this.Routing.AccountPerformers}/${this.Routing.Create}`
    ).catch();
  }

  /** Navigate to create page with name prefill (from input, 1–200 chars). */
  goToCreatePerformer($event: Event): void {
    $event.preventDefault();
    if (this.isMaxPerformersReached) return;
    const name = (this.newPerformerName || '').trim().slice(0, 200);
    const query = name ? `?name=${encodeURIComponent(name)}` : '';
    this.router.navigateByUrl(
      `/${this.Routing.Account}/${this.Routing.AccountPerformers}/${this.Routing.Create}${query}`
    ).catch();
  }
}
